import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {makeMapStateToProps, ResolveStateProps} from '../../../helpers/reselectHelpers';
import {AppState} from '../../../store/appState';
import {getTags} from '../../../store/data/tags/tagsSelectors';
import {OkCancelDialog} from '../../common/dialogs/okCancelDialog';

interface NestingTeamTagDialogConfirmationContainerProps {
  targetTagId: number;
  newParentTagId: number | undefined;
  onRequestClose: Handler;
  onOkClick: Handler;
  shouldNestNextToParent?: boolean;
  isEditingWorkspace?: boolean;
}

function getTargetTagId(_state: AppState, props: NestingTeamTagDialogConfirmationContainerProps) {
  return props.targetTagId;
}

function getNewParentTagId(_state: AppState, props: NestingTeamTagDialogConfirmationContainerProps) {
  return props.newParentTagId;
}

function getShouldNestNextToParent(_state: AppState, props: NestingTeamTagDialogConfirmationContainerProps) {
  return props.shouldNestNextToParent;
}

function makeGetTargetTag() {
  return createSelector([getTargetTagId, getTags], (targetTagId, tags) => tags[targetTagId]);
}

function makeGetTargetParentTag() {
  return createSelector([getTargetTagId, getTags], (targetTagId, tags) => {
    const targetTag = tags[targetTagId];

    if (!targetTag || !targetTag.parentTagId) {
      return undefined;
    }

    return tags[targetTag.parentTagId];
  });
}

function makeGetNewParentTag() {
  return createSelector(
    [getNewParentTagId, getTags, getShouldNestNextToParent],
    (newParentTagId, tags, shouldNestNextToParent) => {
      const newParent = newParentTagId && tags[newParentTagId];

      if (!newParent) {
        return undefined;
      }

      if (newParent.parentTagId && shouldNestNextToParent) {
        return tags[newParent.parentTagId];
      }

      return newParent;
    },
  );
}

const mapStateToProps = makeMapStateToProps<NestingTeamTagDialogConfirmationContainerProps>()(() => ({
  targetTag: makeGetTargetTag(),
  newParentTag: makeGetNewParentTag(),
  targetParentTag: makeGetTargetParentTag(),
}));

interface NestingTeamTagDialogConfirmationContainerComponentProps
  extends NestingTeamTagDialogConfirmationContainerProps,
    ResolveStateProps<typeof mapStateToProps> {}

const NestingTeamTagDialogConfirmationContainerComponent: FC<
  NestingTeamTagDialogConfirmationContainerComponentProps
> = (props) => {
  if (!props.targetTag) {
    return null;
  }

  function renderBody() {
    if (!props.targetTag) {
      return null;
    }

    if (props.newParentTag) {
      return (
        <Trans id="nestingteamtagdialog-nesting-body">
          <strong>{props.targetTag.name}</strong> is a team tag. Would you like to nest it under{' '}
          <strong>{props.newParentTag.name}</strong> for all teammates who have access to this tag?
        </Trans>
      );
    }

    if (props.targetParentTag) {
      return (
        <Trans id="nestingteamtagdialog-unnesting-body">
          <strong>{props.targetTag.name}</strong> is a team tag. Would you like to unnest it from{' '}
          <strong>{props.targetParentTag.name}</strong> for all teammates who have access to this tag?
        </Trans>
      );
    }

    return null;
  }

  const body = renderBody();

  if (!body) {
    return null;
  }

  return (
    <OkCancelDialog
      title={
        <Trans id="nestingteamtagdialog-title">Update nesting for &quot;{props.targetTag.name}&quot;</Trans>
      }
      onOkClick={props.onOkClick}
      onRequestClose={props.onRequestClose}
    >
      {body}
    </OkCancelDialog>
  );
};

export const NestingTeamTagDialogConfirmationContainer = connect(mapStateToProps)(
  NestingTeamTagDialogConfirmationContainerComponent,
);
